/**
 * SeeMe Spotlight Search
 * Command palette / search overlay triggered by Cmd+K.
 *
 * Configure:
 *   SeeMe.spotlight.configure({ url: '/search/', placeholder: 'Search tasks...' })
 *
 * Open/close:
 *   SeeMe.spotlight.open()
 *   SeeMe.spotlight.close()
 *   SeeMe.spotlight.isOpen()
 *
 * Keyboard:
 *   Cmd+K / Ctrl+K — open spotlight
 *   Escape — close spotlight (takes priority over overlay system)
 *
 * The spotlight fetches results from a configurable URL and renders server HTML
 * into the results container. It manages its own backdrop layer so it can overlay
 * on top of existing modals/sheets without interfering with SeeMe.overlay.
 *
 * Result interaction:
 *   - Links (a[href]) close the spotlight and navigate normally
 *   - Filter checkboxes ([data-search-filter]) re-trigger the search
 *   - For custom click handling (e.g. opening sheets), add listeners via
 *     SeeMe.spotlight.onSelect or handle clicks on the results container
 */

var SeeMe = window.SeeMe || {};

SeeMe.spotlight = (function () {

    var el = null;
    var backdropEl = null;
    var searchTimer = null;
    var activeXhr = null;
    var savedBodyOverflow = null;

    var config = {
        url: '/search/',
        placeholder: 'Search...',
        debounce: 200,
        minChars: 2,
        paramName: 'q',
        onSelect: null     // function(e, el) — called on result click
    };

    function configure(opts) {
        for (var key in opts) {
            if (opts.hasOwnProperty(key)) config[key] = opts[key];
        }
    }

    function doSearch(input) {
        var q = input.value.trim();
        clearTimeout(searchTimer);
        if (activeXhr) { activeXhr.abort(); activeXhr = null; }

        var results = el.querySelector('.spotlight__results');
        if (q.length < config.minChars) {
            results.innerHTML = '';
            return;
        }

        searchTimer = setTimeout(function () {
            var params = config.paramName + '=' + encodeURIComponent(q);

            // Include any filter checkboxes rendered in results
            results.querySelectorAll('input[type="checkbox"][name]').forEach(function (cb) {
                params += '&' + cb.name + '=' + (cb.checked ? '1' : '0');
            });

            activeXhr = new XMLHttpRequest();
            activeXhr.open('GET', config.url + '?' + params);
            activeXhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            activeXhr.onload = function () {
                activeXhr = null;
                if (this.status >= 200 && this.status < 300) {
                    results.innerHTML = this.responseText;
                }
            };
            activeXhr.onerror = function () { activeXhr = null; };
            activeXhr.send();
        }, config.debounce);
    }

    function open() {
        if (el) {
            var inp = el.querySelector('.spotlight__input');
            if (inp) inp.focus();
            return;
        }

        // Backdrop — separate from overlay system so spotlight layers on top
        backdropEl = document.createElement('div');
        backdropEl.className = 'backdrop show spotlight-backdrop';
        document.body.appendChild(backdropEl);

        // Modal shell
        el = document.createElement('div');
        el.className = 'modal modal--spotlight show';
        el.innerHTML =
            '<div class="modal__dialog">' +
                '<div class="spotlight__search-bar">' +
                    '<i class="fa-solid fa-magnifying-glass spotlight__icon"></i>' +
                    '<input type="text" class="spotlight__input" placeholder="' +
                        config.placeholder.replace(/"/g, '&quot;') + '" autocomplete="off">' +
                    '<kbd>ESC</kbd>' +
                '</div>' +
                '<div class="spotlight__results"></div>' +
            '</div>';

        document.body.appendChild(el);

        // Lock scroll (preserve existing state for restore)
        savedBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        var input = el.querySelector('.spotlight__input');
        input.focus();

        // Search on input
        input.addEventListener('input', function () { doSearch(input); });

        // Re-search when filter checkboxes toggle
        el.addEventListener('change', function (e) {
            if (!e.target.matches('[data-search-filter]')) return;
            doSearch(input);
        });

        // Close on backdrop click (stopPropagation prevents overlay system interference)
        backdropEl.addEventListener('click', function (e) {
            e.stopPropagation();
            close();
        });

        // Close on click outside dialog
        el.addEventListener('click', function (e) {
            if (e.target === el) {
                e.stopPropagation();
                close();
            }
        });

        // Result click handling
        el.addEventListener('click', function (e) {
            // Custom handler gets first shot
            if (config.onSelect) {
                var item = e.target.closest('.spotlight__results > *, .spotlight__results li, .spotlight__results a');
                if (item) {
                    var handled = config.onSelect(e, item);
                    if (handled === false) return;
                }
            }

            // Default: close on link click (navigating away)
            var link = e.target.closest('a[href]');
            if (link && link.getAttribute('href') !== '#') {
                close();
            }
        });
    }

    function close() {
        if (activeXhr) { activeXhr.abort(); activeXhr = null; }
        clearTimeout(searchTimer);
        if (el) { el.remove(); el = null; }
        if (backdropEl) { backdropEl.remove(); backdropEl = null; }
        document.body.style.overflow = savedBodyOverflow || '';
        savedBodyOverflow = null;
    }

    function isOpen() {
        return !!el;
    }

    // Keyboard — capture phase so spotlight takes priority over overlay system
    document.addEventListener('keydown', function (e) {
        // Escape — close spotlight (prevent overlay system from also firing)
        if (e.key === 'Escape' && el) {
            e.preventDefault();
            e.stopImmediatePropagation();
            close();
            return;
        }

        // Cmd+K / Ctrl+K — open spotlight
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            open();
        }
    }, true);

    return { open: open, close: close, isOpen: isOpen, configure: configure };

})();

window.SeeMe = SeeMe;
